import React, { useState, useEffect, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Table,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import ReportActions from '@Actions/reportActions';
import H3Text from '@Uilib/h3Text';
import H3DateSelector from '@Uilib/h3DateSelector';
import './style.scss';
import { QUANTITY } from '@Apis/constants';
import Helpers from '@Apis/helpers';

const CustomerSalesLedger = ({
  user,
  MONEY,
  getDownloadReports,
  getReports,
  getReportsLoading,
  salesLedger,
  selectedCustomer,
}) => {
  const [selectedInterval, setSelectedInterval] = useState('this_month');
  const [startDateMomentEpoch, setStartDateMomentEpoch] = useState(dayjs().startOf('month').valueOf());
  const [endDateMomentEpoch, setEndDateMomentEpoch] = useState(dayjs().endOf('month').valueOf());
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isUserReady, setIsUserReady] = useState(false);
  const salesLedgerData = salesLedger?.purchase_ledger;

  const salesLedgerColumns = [
    {
      title: 'DATE',
      fixed: 'left',
      render: (record) => <H3Text text={record?.invoice_date ? record?.invoice_date : ''} className="prod-table__text" />,
    },
    {
      title: 'PRODUCT NAME',
      width: '250px',
      render: (record) => <H3Text text={record?.product_sku_name} className="prod-table__text" />,
    },
    {
      title: 'TAX',
      render: (record) => <H3Text text={`${record?.tax_value || 0}%`} className="prod-table__text" />,
    },
    {
      title: 'SO NUMBER',
      render: (record) => <H3Text text={record?.sales_order_number || '-'} className="prod-table__text" />,
    },
    {
      title: 'INVOICE NUMBER',
      render: (record) => <H3Text text={record?.invoice_number} className="prod-table__text" />,
    },
    {
      title: 'QUANTITY',
      render: (record) => (
        <Fragment>
          <div>
            <div>
              {`Order - ${QUANTITY((record?.so_quantity || 0), Number(record?.precision))} ${record?.uqc?.toProperCase() || ''}`}
            </div>
            <div>
              {`Delivered - ${QUANTITY((record?.inv_quantity || 0), Number(record?.precision))} ${record?.uqc?.toProperCase() || ''}`}
            </div>
            <div>
              {`Returned - ${QUANTITY((record?.returned_quantity || 0), Number(record?.precision))} ${record?.uqc?.toProperCase() || ''}`}
            </div>
          </div>
        </Fragment>
      ),
    },
    {
      title: 'SO UNIT PRICE',
      render: (record) => (
        <div>
          {MONEY(record?.so_unit_price || '')}
          <H3Text text={`${MONEY(record?.so_unit_price * (1 + (record?.tax_value / 100)))} incl. ${record?.tax_value}% Tax`} className="table-subscript" />
        </div>
      ),
    },
    {
      title: 'INVOICE UNIT PRICE',
      render: (record) => (
        <div>
          {MONEY(record?.inv_line_unit_price || '')}
          <H3Text text={`${MONEY(record?.inv_line_unit_price * (1 + (record?.tax_value / 100)))} incl. ${record?.tax_value}% Tax`} className="table-subscript" />
        </div>
      ),
    },
    {
      title: 'INVOICE LINE TOTAL',
      render: (record) => MONEY(record?.inv_line_total || '0'),
    },
  ];

  useEffect(() => {
    if (user?.tenant_info?.org_id && selectedCustomer?.customer_id && !isUserReady) {
      getReports('sales_ledger', '', user?.tenant_info?.org_id, user?.tenant_info?.tenant_id, dayjs().startOf('month').valueOf(), dayjs().endOf('month').valueOf(), 'sales_ledger', 1, 30, '', '', '', selectedCustomer?.customer_id);
      setIsUserReady(true);
    }
  }, [isUserReady, user, selectedCustomer]);

  function downloadReportData() {
    const { fileBase64 } = salesLedger;
    Helpers.downloadFileFromBase64(fileBase64, 'sales_ledger.xlsx');
  }

  const handleDataChange = (value) => {
    // Handle interval change
    setSelectedInterval(value);
  };

  return (
    <Fragment>
      <div className="customer-pos__wrapper">
        <div className="ant-row">
          <div className="ant-col-md-24 ant-col-xs-24">
            <div className="generic-table-container">
              <Table
                title={() => (
                  <div className="sales-ledger__table-header">
                    <div className="sales-ledger__table-header__left">
                      {`${salesLedgerData?.length || '0'} Transactions`}
                    </div>
                    <div className="sales-ledger__table-header__right">
                      {salesLedgerData?.length > 0 && (
                        <div className="report-header__action-btn" onClick={() => downloadReportData()}>
                          <H3Text
                            text={(
                              <div>
                                <DownloadOutlined />
                              </div>
                            )}
                            className="action-button"
                          />
                        </div>
                      )}
                      <div className="">
                        <H3DateSelector
                          selectedInterval={selectedInterval}
                          onListChange={(value) => handleDataChange(value)}
                          startDate={startDateMomentEpoch}
                          endDate={endDateMomentEpoch}
                          onDateChange={(date) => {
                            setStartDateMomentEpoch(dayjs(date[0])?.valueOf());
                            setEndDateMomentEpoch(dayjs(date[1])?.valueOf());
                            setSelectedInterval('customize');
                            getReports('sales_ledger', '', user?.tenant_info?.org_id, user?.tenant_info?.tenant_id, dayjs(date[0])?.valueOf(), dayjs(date[1])?.valueOf(), 'sales_ledger', 1, 30, '', '', '', selectedCustomer?.customer_id);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                loading={getReportsLoading}
                showHeader
                size="small"
                scroll={{ x: 'max-content' }}
                columns={salesLedgerColumns}
                rowKey="po_id"
                dataSource={salesLedgerData || []}
                pagination={{
                  current: page,
                  total: salesLedgerData?.length || 0,
                  pageSize: limit,
                  position: ['bottomRight'],
                  showSizeChanger: false,
                  onChange: (pg, size) => {
                    setPage(pg);
                    setLimit(size);
                  },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, ReportReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  salesLedger: ReportReducers.salesLedger,
  getReportsLoading: ReportReducers.getReportsLoading,
  getDownloadReportsLoading: ReportReducers.getDownloadReportsLoading,
});

const mapDispatchToProps = (dispatch) => ({
  getReports: (firstSubUrl, secondSubUrl, orgId, tenantIds, fromDate, toDate, reportType, page, limit, departmentId, sellerId, costingMethod, customerId) => dispatch(ReportActions.getReports(firstSubUrl, secondSubUrl, orgId, tenantIds, fromDate, toDate, reportType, page, limit, departmentId, sellerId, costingMethod, customerId)),
  getDownloadReports: (firstSubUrl, secondSubUrl, orgId, tenantIds, fromDate, toDate, fileReportType, reportName, sellerId, departmentId, moType, status, emails, noWait, intervals, noOfColumns, selectedMethod, selectedDate, costingMethod, customerId) => dispatch(ReportActions.getDownloadReports(firstSubUrl, secondSubUrl, orgId, tenantIds, fromDate, toDate, fileReportType, reportName, sellerId, departmentId, moType, status, emails, noWait, intervals, noOfColumns, selectedMethod, selectedDate, costingMethod, customerId)),
});

CustomerSalesLedger.propTypes = {
  user: PropTypes.any,
  MONEY: PropTypes.func,
  getDownloadReports: PropTypes.func,
  getReports: PropTypes.func,
  getReportsLoading: PropTypes.bool,
  salesLedger: PropTypes.any,
  selectedCustomer: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(CustomerSalesLedger));
